/**
 * Hook that identifies the current user in analytics and sets super properties.
 * Reacts to changes in auth, org, and billing state to keep properties in sync.
 * Syncs user identity to both Mixpanel and GTM dataLayer.
 * Should be mounted once at the app layout level.
 */

import {
  selectMemberships,
  selectOrganization,
  selectUser,
} from '@saaga/shared/store/slices/auth.slice'
import {
  selectIsAutoTopUpEnabled,
  selectSeatCount,
  selectTenant,
} from '@saaga/shared/store/slices/billing.slice'
import { useAppSelector } from '@saaga/shared/store/store'
import { initAnalytics, setSuperProperties } from '@saaga/shared/utils/analytics.util'
import { useEffect } from 'react'

import { useAnalyticsIdentitySync } from './use-analytics-identity-sync.hook'
import { useGtmUserSync } from './use-gtm-user-sync.hook'

const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN ?? ''

const resolveEnvironment = (): string => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
  if (hostname === 'localhost') return 'development'
  if (hostname.includes('dev') || hostname.includes('staging')) return 'staging'
  return 'production'
}

export const useAnalyticsIdentify = (): void => {
  const user = useAppSelector(selectUser)
  const organization = useAppSelector(selectOrganization)
  const membershipList = useAppSelector(selectMemberships)
  const tenant = useAppSelector(selectTenant)
  const seatCount = useAppSelector(selectSeatCount)
  const isAutoTopUpEnabled = useAppSelector(selectIsAutoTopUpEnabled)

  useEffect(() => {
    initAnalytics(MIXPANEL_TOKEN)
  }, [])

  useGtmUserSync(user, organization)
  useAnalyticsIdentitySync(user, organization)

  useEffect(() => {
    if (!user || !organization) return

    const currentMembership = membershipList.find((m) => m.organizationId === organization.id)

    setSuperProperties({
      userId: user.id,
      orgId: organization.id,
      orgSlug: organization.slug ?? '',
      userRole: currentMembership?.role ?? 'unknown',
      environment: resolveEnvironment(),
      planName: tenant?.subscriptionName ?? null,
      hasPaymentMethod: tenant?.defaultPaymentMethod !== null,
      seatCount,
      isAutoTopUpEnabled,
    })
  }, [user, organization, membershipList, tenant, seatCount, isAutoTopUpEnabled])
}
